import { useEffect, useRef, useCallback, useReducer } from 'react';
import { useSocket, useSocketEvent } from 'socket.io-react-hook';
import { _getToken } from 'src/Config/axiosApi';
import { EnrichedRuleStatus, OrdersResponse } from '../types/unwinding.types';
export type { EnrichedRuleStatus } from '../types/unwinding.types';

export interface UnwindExecutionEvent {
  ruleId: string;
  pairName: string;
  makerPlatform: string;
  makerPrice: number;
  counterPrice: number;
  contracts: number;
  tnaPercent: number;
  volumeUsd: number;
}

export interface UnwindErrorEvent {
  ruleId: string;
  pairName: string;
  state: string;
  errorMessage: string;
}

type Action =
  | { type: 'STATUS'; payload: EnrichedRuleStatus[] }
  | { type: 'STATUS_UPDATE'; payload: EnrichedRuleStatus[] }
  | { type: 'EXECUTION'; payload: UnwindExecutionEvent }
  | { type: 'ERROR'; payload: UnwindErrorEvent }
  | { type: 'ORDERS'; payload: OrdersResponse };

interface State {
  /** Map of ruleId -> live order state */
  states: Map<string, EnrichedRuleStatus>;
  /** Most recent execution event (for toasts/notifications) */
  lastExecution: UnwindExecutionEvent | null;
  /** Most recent error event */
  lastError: UnwindErrorEvent | null;
  orders: OrdersResponse | null;
}

const initialState: State = {
  states: new Map(),
  lastExecution: null,
  lastError: null,
  orders: null,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'STATUS': {
      // Full snapshot — replace entire map
      const newMap = new Map<string, EnrichedRuleStatus>();
      for (const s of action.payload) {
        newMap.set(s.ruleId, s);
        if (s.pairId && s.pairId !== s.ruleId) newMap.set(s.pairId, s);
      }
      return { ...state, states: newMap };
    }
    case 'STATUS_UPDATE': {
      // Partial update — merge into existing map
      const merged = new Map(state.states);
      for (const s of action.payload) {
        merged.set(s.ruleId, s);
        if (s.pairId && s.pairId !== s.ruleId) merged.set(s.pairId, s);
      }
      return { ...state, states: merged };
    }
    case 'EXECUTION':
      return { ...state, lastExecution: action.payload };
    case 'ERROR':
      return { ...state, lastError: action.payload };
    case 'ORDERS':
      return { ...state, orders: action.payload };
    default:
      return state;
  }
}

export function useUnwindingSocket() {
  const wsUrl = import.meta.env.VITE_POSITION_UNWINDER_URL;
  console.log('[UnwindSocket] Init, URL:', wsUrl);
  const accessToken = _getToken();

  const { socket } = useSocket(
    (wsUrl || '') + '/unwind',
    {
      enabled: !!wsUrl && !!accessToken,
      transports: ['websocket'],
      // Pass auth as a callback so socket.io-client reads the current
      // token on every (re)connection. This ensures logout/refresh
      // updates propagate and we never reconnect with a stale token.
      auth: (cb: (data: { token: string }) => void) =>
        cb({ token: _getToken() }),
    }
  );

  const [state, dispatch] = useReducer(reducer, initialState);
  const hasSubscribed = useRef(false);

  // Subscribe on connect
  useEffect(() => {
    console.log('[UnwindSocket] connected:', socket.connected, 'id:', socket.id, 'url:', import.meta.env.VITE_POSITION_UNWINDER_URL);
    if (!socket.connected) {
      hasSubscribed.current = false;
      return;
    }
    if (!hasSubscribed.current) {
      socket.emit('subscribe_unwind');
      hasSubscribed.current = true;
      console.log('[UnwindSocket] Subscribed to unwind updates');
    }
  }, [socket.connected]);

  // Listen to events
  const { lastMessage: statusMessage } = useSocketEvent<EnrichedRuleStatus[]>(
    socket,
    'unwind_status'
  );

  const { lastMessage: statusUpdateMessage } = useSocketEvent<EnrichedRuleStatus[]>(
    socket,
    'unwind_status_update'
  );

  const { lastMessage: executionMessage } =
    useSocketEvent<UnwindExecutionEvent>(socket, 'unwind_execution');

  const { lastMessage: errorMessage } = useSocketEvent<UnwindErrorEvent>(
    socket,
    'unwind_error'
  );

  // Handle full snapshot
  useEffect(() => {
    if (statusMessage) {
      console.log('[UnwindSocket] Status received:', Array.isArray(statusMessage) ? `${statusMessage.length} states` : typeof statusMessage);
      dispatch({ type: 'STATUS', payload: statusMessage });
    }
  }, [statusMessage]);

  // Handle partial updates (individual market changes)
  useEffect(() => {
    if (statusUpdateMessage) {
      dispatch({ type: 'STATUS_UPDATE', payload: statusUpdateMessage });
    }
  }, [statusUpdateMessage]);

  useEffect(() => {
    if (executionMessage) {
      dispatch({ type: 'EXECUTION', payload: executionMessage });
    }
  }, [executionMessage]);

  useEffect(() => {
    if (errorMessage) {
      dispatch({ type: 'ERROR', payload: errorMessage });
    }
  }, [errorMessage]);

  const { lastMessage: ordersMessage } = useSocketEvent<OrdersResponse>(
    socket,
    'unwind_orders'
  );

  useEffect(() => {
    if (ordersMessage) {
      dispatch({ type: 'ORDERS', payload: ordersMessage });
    }
  }, [ordersMessage]);

  const getState = useCallback(
    (ruleId: string): EnrichedRuleStatus | undefined => state.states.get(ruleId),
    [state.states]
  );

  /** Manually inject enriched states (e.g., from REST response as WS fallback) */
  const injectStates = useCallback((states: EnrichedRuleStatus[]) => {
    dispatch({ type: 'STATUS_UPDATE', payload: states });
  }, []);

  return {
    isConnected: socket.connected,
    states: state.states,
    getState,
    injectStates,
    lastExecution: state.lastExecution,
    lastError: state.lastError,
    orders: state.orders,
  };
}
